import { CustomError } from '@apeiron/temp'
import { ERROR_CODE } from '@src/constants/error'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useBeforeunload } from 'react-beforeunload'

const usePreventLeave = (props: Props): Hook => {
  const { haveChanges } = props

  const router = useRouter()

  const [showExitModal, setShowExitModal] = useState<boolean>(false)

  const [nextPath, setNextPath] = useState<string | null>(null)

  const reset = useCallback(() => {
    setShowExitModal(false)
    setNextPath(null)
  }, [])

  const reloadStart = useCallback(
    (event: Event) => {
      if (haveChanges()) {
        event.preventDefault()
      }
    },
    [haveChanges]
  )

  const routeChangeStart = useCallback(
    (path: string) => {
      if (R.isNil(nextPath) && haveChanges()) {
        setShowExitModal(true)
        setNextPath(path)

        throw new CustomError(
          'abort route change (please ignore this error)',
          ERROR_CODE.UI.ABORT_ROUTE_CHANGE
        )
      } else {
        reset()
      }
    },
    [haveChanges, nextPath, reset]
  )

  const performChange = useCallback(() => {
    if (!R.isNil(nextPath)) {
      router.push(nextPath)
    }
  }, [nextPath, router])

  useBeforeunload(reloadStart)

  useEffect(() => {
    router.events.on('routeChangeStart', routeChangeStart)

    return () => {
      router.events.off('routeChangeStart', routeChangeStart)
    }
  }, [routeChangeStart])

  return {
    nextPath,
    showExitModal,
    performChange,
    reset
  }
}

type Hook = {
  nextPath: string | null
  showExitModal: boolean
  performChange: () => void
  reset: () => void
}

type Props = {
  haveChanges: () => boolean
}

export default usePreventLeave
